function getAudioContextClass(): typeof AudioContext | null {
  if (typeof window === "undefined") return null;
  return (
    window.AudioContext ||
    (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext ||
    null
  );
}

type ParticipantAudioEntry = {
  gain: GainNode;
  sources: MediaStreamAudioSourceNode[];
};

const muteListeners = new Set<(id: string) => void>();

function createLivestreamAudioSink() {
  let audioContext: AudioContext | null = null;
  let destination: MediaStreamAudioDestinationNode | null = null;
  let sharedAudioElement: HTMLAudioElement | null = null;
  let unlocked = false;

  const entries = new Map<string, ParticipantAudioEntry>();
  const mutedIds = new Set<string>();

  const ensureContext = (): AudioContext | null => {
    if (audioContext) return audioContext;
    const AudioCtx = getAudioContextClass();
    if (!AudioCtx) return null;

    audioContext = new AudioCtx();
    destination = audioContext.createMediaStreamDestination();

    if (typeof document !== "undefined") {
      sharedAudioElement = document.createElement("audio");
      sharedAudioElement.autoplay = true;
      sharedAudioElement.setAttribute("playsinline", "");
      sharedAudioElement.srcObject = destination.stream;
      sharedAudioElement.style.display = "none";
      document.body.appendChild(sharedAudioElement);
    }

    return audioContext;
  };

  const disconnectEntry = (entry: ParticipantAudioEntry) => {
    entry.sources.forEach((source) => {
      try {
        source.disconnect();
      } catch {
        // ignore
      }
    });
    try {
      entry.gain.disconnect();
    } catch {
      // ignore
    }
  };

  return {
    isUnlocked(): boolean {
      return unlocked;
    },

    isMuted({ id }: { id: string }): boolean {
      return mutedIds.has(id);
    },

    /**
     * Must be called synchronously inside a user-gesture handler so the shared
     * audio element gets "blessed" once. Every participant connected afterward
     * is then audible without needing a further gesture.
     */
    unlock() {
      const context = ensureContext();
      if (context && context.state === "suspended") {
        void context.resume();
      }
      if (sharedAudioElement) {
        sharedAudioElement.muted = false;
        void sharedAudioElement.play().catch(() => {});
      }
      unlocked = true;
    },

    connect({ id, tracks }: { id: string; tracks: MediaStreamTrack[] }) {
      const context = ensureContext();
      if (!context || !destination) return;

      this.disconnect({ id });

      if (tracks.length === 0) return;

      const gain = context.createGain();
      gain.gain.value = mutedIds.has(id) ? 0 : 1;
      gain.connect(destination);

      const sources = tracks.map((track) => {
        const source = context.createMediaStreamSource(new MediaStream([track]));
        source.connect(gain);
        return source;
      });

      entries.set(id, { gain, sources });

      if (context.state === "suspended") {
        void context.resume();
      }
      if (unlocked && sharedAudioElement) {
        void sharedAudioElement.play().catch(() => {});
      }
    },

    disconnect({ id }: { id: string }) {
      const entry = entries.get(id);
      if (!entry) return;
      disconnectEntry(entry);
      entries.delete(id);
    },

    setMuted({ id, muted }: { id: string; muted: boolean }) {
      if (muted) {
        mutedIds.add(id);
      } else {
        mutedIds.delete(id);
      }
      const entry = entries.get(id);
      if (entry) {
        entry.gain.gain.value = muted ? 0 : 1;
      }
      muteListeners.forEach((listener) => listener(id));
    },

    toggleMuted({ id }: { id: string }): boolean {
      const nextMuted = !mutedIds.has(id);
      this.setMuted({ id, muted: nextMuted });
      return nextMuted;
    },

    subscribeMuted(listener: (id: string) => void): () => void {
      muteListeners.add(listener);
      return () => {
        muteListeners.delete(listener);
      };
    },
  };
}

export const livestreamAudioSink = createLivestreamAudioSink();
